import { Construction, AlertCircle, ArrowRight } from 'lucide-react';
import type { RepositoryAnalysis } from '../types/analysis.types';

interface GapsDetailedSectionProps {
  data: RepositoryAnalysis;
}

export const GapsDetailedSection: React.FC<GapsDetailedSectionProps> = ({ data }) => {
  const incomplete = data?.gapsDetailed?.incomplete || [];
  const risks = data?.gapsDetailed?.risks || [];

  if (incomplete.length === 0 && risks.length === 0) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Incomplete Practices Card */}
        {incomplete.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xs overflow-hidden border-l-4 border-l-amber-500 border border-slate-200 dark:border-slate-700/50">
            <div className="p-5 border-b border-slate-200 dark:border-slate-700/50 bg-amber-50 dark:bg-amber-900/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                    <Construction className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-primary">Incomplete Practices</h2>
                </div>
                <span className="px-3 py-1 text-sm font-medium rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                  {incomplete.length}
                </span>
              </div>
              <p className="text-sm text-secondary mt-2 ml-12">
                Practices that are partially implemented
              </p>
            </div>

            <div className="p-5">
              <ul className="space-y-4 max-h-96 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
                {incomplete.map((item) => (
                  <li
                    key={item.practice}
                    className="p-4 rounded-lg border border-amber-200 dark:border-amber-800/30 bg-amber-50/50 dark:bg-amber-900/10"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-primary">{item.practice}</span>
                      <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
                        {item.category}
                      </span>
                    </div>

                    {/* Current vs Target State */}
                    <div className="flex items-center gap-3 text-sm">
                      <div className="flex-1 p-2 rounded bg-slate-100 dark:bg-slate-800/50">
                        <div className="text-xs text-secondary mb-0.5">Current</div>
                        <div className="text-primary font-medium">{item.currentState}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-amber-500 shrink-0" />
                      <div className="flex-1 p-2 rounded bg-green-50 dark:bg-green-900/20">
                        <div className="text-xs text-secondary mb-0.5">Target</div>
                        <div className="text-green-600 dark:text-green-400 font-medium">
                          {item.targetState}
                        </div>
                      </div>
                    </div>

                    {item.effort && (
                      <div className="mt-2 text-xs text-secondary">
                        Estimated effort:{' '}
                        <span className="font-semibold text-primary">{item.effort}</span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Identified Risks Card */}
        {risks.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xs overflow-hidden border-l-4 border-l-orange-500 border border-slate-200 dark:border-slate-700/50">
            <div className="p-5 border-b border-slate-200 dark:border-slate-700/50 bg-orange-50 dark:bg-orange-900/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                    <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-primary">Identified Risks</h2>
                </div>
                <span className="px-3 py-1 text-sm font-medium rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                  {risks.length}
                </span>
              </div>
              <p className="text-sm text-secondary mt-2 ml-12">
                Potential issues that need attention
              </p>
            </div>

            <div className="p-5">
              <ul className="space-y-4 max-h-96 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
                {risks.map((risk) => (
                  <li
                    key={risk.risk}
                    className="p-4 rounded-lg border border-orange-200 dark:border-orange-800/30 bg-orange-50/50 dark:bg-orange-900/10"
                  >
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="font-semibold text-primary">{risk.risk}</span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          risk.probability === 'high'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                            : risk.probability === 'medium'
                              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        {risk.probability} probability
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full">
                        {risk.category}
                      </span>
                    </div>

                    {risk.impact && (
                      <div className="text-sm mb-2">
                        <span className="text-secondary">Impact: </span>
                        <span className="text-primary">{risk.impact}</span>
                      </div>
                    )}

                    {risk.mitigation && (
                      <div className="text-sm p-2 rounded bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30">
                        <span className="text-green-700 dark:text-green-400 font-medium">
                          Mitigation:{' '}
                        </span>
                        <span className="text-green-600 dark:text-green-300">
                          {risk.mitigation}
                        </span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GapsDetailedSection;
